/**
 * Wholesale account phone on users/{uid}.
 *
 * Canonical Firestore field: `contactInfo` (string).
 * Legacy / alternate field: `phone` (string) — still present on some docs and address blobs.
 * Always read with resolveUserPhone; always write both keys in sync so Account, Admin cards,
 * place-on-behalf, and Cart agree.
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizePhoneString(value) {
    if (value == null) return '';
    if (typeof value === 'object') {
        // Rare bad shape: contactInfo stored as { phone: '…' }
        const nested = value.phone ?? value.tel ?? value.mobile ?? value.number;
        return String(nested ?? '').trim();
    }
    return String(value).trim();
}

/**
 * Resolve the account phone from a users doc or address-like object.
 * Prefers `phone`, then `contactInfo` (string or nested).
 * @param {object | null | undefined} userOrAddress
 * @returns {string}
 */
export function resolveUserPhone(userOrAddress) {
    if (!userOrAddress || typeof userOrAddress !== 'object') return '';
    const fromPhone = normalizePhoneString(userOrAddress.phone);
    if (fromPhone) return fromPhone;
    return normalizePhoneString(userOrAddress.contactInfo);
}

/**
 * Merge phone onto a Firestore write payload (both keys, same value).
 * @param {object} payload
 * @param {string} [phoneValue] — if omitted, taken from payload.phone || payload.contactInfo
 * @returns {object}
 */
export function applyUserPhoneToWritePayload(payload = {}, phoneValue) {
    const next = { ...(payload || {}) };
    const resolved =
        phoneValue !== undefined
            ? normalizePhoneString(phoneValue)
            : resolveUserPhone(next);
    next.contactInfo = resolved;
    next.phone = resolved;
    return next;
}

/**
 * UI form row: ensure `phone` is filled from either stored field.
 * @param {object} user
 * @returns {object}
 */
export function withResolvedPhoneForUi(user) {
    if (!user || typeof user !== 'object') return user;
    const phone = resolveUserPhone(user);
    return { ...user, phone, contactInfo: phone || normalizePhoneString(user.contactInfo) };
}
